import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { getConditionsSettings } from '../../utils';

export default function ContactForm() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const infos = useOutletContext();
  const [searchParams] = useSearchParams();
  const { settings } = infos;

  const { contact: contactSettings } = settings;
  const { companyID, operationId } = contactSettings;

  const { contactApiUrl } = getConditionsSettings(settings);

  const getInitialCommentaire = () => {
    const prixLotParams = searchParams.get('prix_lot');
    const lotParams = searchParams.get('lot');
    const reservationParams = searchParams.get('reservation_lot');

    if (prixLotParams) {
      return `Je souhaiterais plus de renseignements sur le prix du lot ${prixLotParams}`;
    } else if (lotParams) {
      return `Je souhaiterais plus de renseignements sur le lot ${lotParams}`;
    } else if (reservationParams) {
      return `Je souhaiterais plus de renseignements pour la réservation du lot ${reservationParams}`;
    } else {
      return '';
    }
  };

  const defaultValues = {
    civilite: 'Mr',
    nom: '',
    prenom: '',
    email: '',
    tel: '',
    preferences_actions_commerciales: false,
    send_confirmations: '',
    complement_message_client: getInitialCommentaire(),
    complement_origine: 30,
    noAlert: false,
    contactType: 'contact',
    companyID,
    operationId,
    financement: [],
    preferences_programmes: '',
  };

  const [formValues, setFormValues] = useState(defaultValues);
  const [loading, setLoading] = useState(false);
  const [postResult, setPostResult] = useState(null);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const newValues = {
      ...formValues,
      [name]: value,
    };
    setFormValues(newValues);
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    const newValues = {
      ...formValues,
      [name]: checked,
    };
    setFormValues(newValues);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!executeRecaptcha) {
      console.error('Recaptcha has not been loaded');
      return;
    }

    setLoading(true);
    const recaptchaToken = await executeRecaptcha();

    const bodyData = {
      token: {
        captchaToken: recaptchaToken,
        authID: 'none',
        authType: 'none',
      },
      body: formValues,
    };

    try {
      const res = await fetch(contactApiUrl, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bodyData),
      });

      if (!res.ok) {
        const message = `Une erreur s'est produite : ${res.status} - ${res.statusText}`;
        setLoading(false);
        setPostResult({ success: false, message });
        return;
      }
      setLoading(false);
      setPostResult({ success: true, message: 'Message envoyé avec succès' });
    } catch (err) {
      setLoading(false);
      setPostResult({
        success: false,
        message: `Une erreur s'est produite`,
      });
    }
  };

  return (
    <Stack
      component="form"
      direction="column"
      spacing={2}
      onSubmit={handleSubmit}
    >
      <Stack direction="row" spacing={2} mt={5}>
        <FormControl sx={{ flex: 1, maxWidth: '100px' }}>
          <InputLabel id="civilite-select-label">Civ</InputLabel>
          <Select
            labelId="civilite-select-label"
            id="civilite-select"
            name="civilite"
            label="Civ"
            value={formValues.civilite}
            onChange={handleInputChange}
          >
            <MenuItem value={'Mr'}>Mr</MenuItem>
            <MenuItem value={'Mme'}>Mme</MenuItem>
          </Select>
        </FormControl>
        <TextField
          sx={{ flex: 3 }}
          label="Nom"
          name="nom"
          value={formValues.nom}
          onChange={handleInputChange}
        />
      </Stack>
      <TextField
        label="Email"
        name="email"
        type="email"
        value={formValues.email}
        onChange={handleInputChange}
      />
      <TextField
        label="Téléphone"
        name="tel"
        type="tel"
        value={formValues.tel}
        onChange={handleInputChange}
      />
      <TextField
        label="Je suis interessé"
        rows={4}
        multiline
        name="complement_message_client"
        value={formValues.complement_message_client}
        onChange={handleInputChange}
      />

      {postResult ? (
        <Typography
          sx={{
            padding: '12px',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: postResult.success ? 'success.light' : 'error.light',
            backgroundColor: postResult.success
              ? 'success.lighter'
              : 'error.lighter',
          }}
        >
          {postResult.message}
        </Typography>
      ) : null}

      <FormControlLabel
        control={
          <Checkbox
            checked={formValues.preferences_actions_commerciales}
            name="preferences_actions_commerciales"
            onChange={handleCheckboxChange}
          />
        }
        label="Les informations recueillies à partir de ce formulaire sont enregistrées
        et transmises aux personnels de notre entreprise chargés du traitement
        de votre message. Mes données personnelles ne seront jamais communiquées
        à des tiers."
        sx={{ marginTop: '16px', '> span': { fontSize: '0.7rem' } }}
      />

      <Typography sx={{ fontSize: '0.7rem' }}>
        Ce site est protégé par reCAPTCHA et la technologie Google
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noreferrer"
        >
          &nbsp;Politique de confidentialité&nbsp;
        </a>
        et
        <a
          href="https://policies.google.com/terms"
          target="_blank"
          rel="noreferrer"
        >
          &nbsp;Conditions d'utilisation
        </a>
        .
      </Typography>

      <Button
        variant="contained"
        type="submit"
        disabled={
          loading ||
          contactApiUrl === 'undefined' ||
          companyID === 'undefined' ||
          operationId === 'undefined'
        }
        sx={{
          width: 'min-content',
          alignSelf: 'center',
          padding: '12px 26px',
        }}
      >
        Envoyer
      </Button>
    </Stack>
  );
}
